import useLocaleText from 'components/useLocaleText'
import { attachmentMenuText } from './literals'
import React, { useEffect, useRef, useState } from 'react'
import { Camera, File, Image, Plus } from 'lucide-react'
import { DropdownMenu } from 'components/ui'
import { IDropdownOption } from '../Menu/DropdownMenu'
import { CameraCapture } from '../CameraCapture'
import { classNames } from '../classNames'
import theme from '../ChatSurface/styles.module.css'
import styles from './styles.module.css'

export interface AttachmentMenuProps {
  anchor: React.RefObject<HTMLDivElement>
  disabled: boolean
  /** Render inline when the composer is hosted in another dialog through a portal. */
  usePortal?: boolean
  onCameraOpenChange?: (open: boolean) => void
  attachmentsDisabled?: boolean
  onUpload: (file: File) => void
  onUploadFiles?: (files: File[]) => void
  onChoosePhoto?: () => void
  onChooseFile?: () => void
  photoAccept?: string
  fileAccept?: string
  fileLabel?: string
  fileHint?: string
  onOpen?: () => void
  extraOptions?: IDropdownOption<string>[]
  onExtraSelect?: (value: string) => void
  renderCamera?: (onCapture: (file: File) => void, onClose: () => void) => React.ReactNode
  showCamera?: boolean
  showPhoto?: boolean
  showFile?: boolean
  triggerButtonProps?: Record<string, any>
}

export function AttachmentMenu({
  anchor,
  disabled,
  usePortal = true,
  onCameraOpenChange,
  onUpload,
  onUploadFiles,
  onChoosePhoto,
  onChooseFile,
  onOpen,
  extraOptions = [],
  onExtraSelect,
  renderCamera,
  attachmentsDisabled,
  photoAccept = 'image/*',
  fileAccept = '*/*',
  fileLabel,
  fileHint,
  showCamera = true,
  showPhoto = true,
  showFile = true,
  triggerButtonProps,
}: AttachmentMenuProps) {
  const [camera, setCamera] = useState(false)

  const file = useRef<HTMLInputElement>(null)
  const t = useLocaleText(attachmentMenuText)

  const choose = (kind: string) => {
    if (!['camera', 'photo', 'file'].includes(kind)) {
      onExtraSelect?.(kind)

      return
    }

    if (kind === 'camera') {
      setCamera(true)

      return
    }

    if (kind === 'photo' && onChoosePhoto) {
      onChoosePhoto()

      return
    }

    if (kind === 'file' && onChooseFile) {
      onChooseFile()

      return
    }

    if (!file.current) return

    file.current.accept = kind === 'photo' ? photoAccept : fileAccept
    file.current.click()
  }

  useEffect(() => {
    if (disabled || attachmentsDisabled || !showCamera) setCamera(false)
  }, [attachmentsDisabled, disabled, showCamera])

  useEffect(() => {
    onCameraOpenChange?.(camera)
    return () => onCameraOpenChange?.(false)
  }, [camera, onCameraOpenChange])

  return (
    <>
      <input
        ref={file}
        type="file"
        multiple={!!onUploadFiles}
        data-attachment-input
        hidden
        onChange={(event) => {
          const selected = Array.from(event.target.files || [])
          if (onUploadFiles) onUploadFiles(selected)
          else if (selected[0]) onUpload(selected[0])

          event.target.value = ''
        }}
      />
      <DropdownMenu
        usePortal={usePortal}
        anchorRef={anchor}
        menuClassName={classNames(styles.uploadMenu, theme.surface)}
        onOpen={onOpen}
        footer={<p className={styles.uploadHint}>{t.dropHint}</p>}
        placement="top-start"
        maxListHeight={560}
        isDisabled={disabled}
        triggerContent={<Plus size={20} />}
        triggerVariant="icon"
        triggerButtonProps={{
          'aria-label': t.add,
          borderRadius: '50%',
          bg: 'var(--chat-sunken, #f0efeb)',
          ...triggerButtonProps,
        }}
        options={[
          ...(showCamera
            ? [
                {
                  id: 'camera',
                  disabled: attachmentsDisabled,
                  sectionTitle: <p className={styles.sectionTitle}>{t.add}</p>,
                  value: 'camera',
                  icon: <Camera size={22} strokeWidth={2} />,
                  label: (
                    <span className={styles.uploadOption}>
                      {t.takePhoto}
                      <small>{t.cameraHint}</small>
                    </span>
                  ),
                },
              ]
            : []),
          ...(showPhoto
            ? [
                {
                  id: 'photo',
                  disabled: attachmentsDisabled,
                  sectionTitle: !showCamera ? <p className={styles.sectionTitle}>{t.add}</p> : undefined,
                  value: 'photo',
                  icon: <Image size={22} strokeWidth={2} />,
                  label: (
                    <span className={styles.uploadOption}>
                      {t.uploadPhoto}
                      <small>{t.computer}</small>
                    </span>
                  ),
                },
              ]
            : []),
          ...(showFile
            ? [
                {
                  id: 'file',
                  disabled: attachmentsDisabled,
                  sectionTitle: !showCamera && !showPhoto ? <p className={styles.sectionTitle}>{t.add}</p> : undefined,
                  value: 'file',
                  icon: <File size={22} strokeWidth={2} />,
                  label: (
                    <span className={styles.uploadOption}>
                      {fileLabel || t.uploadFile}
                      <small>{fileHint || t.computer}</small>
                    </span>
                  ),
                },
              ]
            : []),
          ...extraOptions,
        ]}
        onSelect={(option) => choose(option.value)}
      />
      {camera &&
        (renderCamera ? (
          renderCamera(onUpload, () => setCamera(false))
        ) : (
          <CameraCapture onCapture={onUpload} onClose={() => setCamera(false)} />
        ))}
    </>
  )
}
